// frontend/src/services/project.service.ts
//
// Proje listesi ve geçmiş gönderimler doğrudan Supabase'den okunur (RLS
// zaten koruyor). Gönderim (submit), Gemini değerlendirmesi + atomik
// XP/kayıt işlemi gerektirdiği için backend'e gider — courses.service.ts
// ile aynı desen.

import { supabase } from '../lib/supabaseClient';
import { authService } from './auth.service';
import {
  Project,
  ProjectWithStatus,
  SubmissionSummary,
  SubmitProjectResponse,
  PersonalizedProjectResult,
} from '../types/project.types';

const API_BASE = (import.meta.env.PROD && (import.meta.env.VITE_API_BASE_URL === 'http://localhost:8080' || !import.meta.env.VITE_API_BASE_URL)) ? '' : (import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080');

class ProjectApiError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ProjectApiError';
  }
}

export const projectService = {
  async listProjects(): Promise<Project[]> {
    const { data, error } = await supabase
      .from('projects')
      .select('id, title, description, difficulty, starter_code, course_id, category, is_published, xp_reward, estimated_duration')
      .or('is_published.eq.true,is_published.is.null')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data ?? [];
  },

  /** Tüm projeleri, öğrencinin en iyi skoruna göre durum etiketiyle birlikte döndürür. */
  async listProjectsWithStatus(studentId: string): Promise<ProjectWithStatus[]> {
    const [projectsRes, submissionsRes] = await Promise.all([
      supabase
        .from('projects')
        .select('id, title, description, difficulty, starter_code, course_id, category, is_published, xp_reward, estimated_duration')
        .or('is_published.eq.true,is_published.is.null')
        .order('created_at', { ascending: false }),
      supabase
        .from('submissions')
        .select('project_id, ai_score')
        .eq('student_id', studentId)
        .not('project_id', 'is', null),
    ]);

    if (projectsRes.error) throw projectsRes.error;
    if (submissionsRes.error) throw submissionsRes.error;

    const bestScoreByProject = new Map<string, number>();
    for (const row of submissionsRes.data ?? []) {
      if (row.ai_score == null) continue;
      const current = bestScoreByProject.get(row.project_id) ?? -1;
      if (row.ai_score > current) bestScoreByProject.set(row.project_id, row.ai_score);
    }

    return (projectsRes.data ?? []).map((project) => {
      const bestScore = bestScoreByProject.get(project.id) ?? null;
      return {
        ...project,
        bestScore,
        status: bestScore === null ? 'not_started' : bestScore >= 60 ? 'completed' : 'in_progress',
      };
    });
  },

  async generatePersonalizedProject(interest: string): Promise<PersonalizedProjectResult> {
    const token = await authService.getAccessToken();
    if (!token) throw new ProjectApiError('Oturum bulunamadı, lütfen giriş yapın.');

    const res = await fetch(`${API_BASE}/api/projects/generate-personalized`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ interest }),
    });
    const data = await res.json();
    if (!res.ok) throw new ProjectApiError(data?.error?.message || 'Kişisel proje üretilemedi.');
    return data.project;
  },

  async getSubmissionHistory(projectId: string): Promise<SubmissionSummary[]> {
    const token = await authService.getAccessToken();
    if (!token) throw new ProjectApiError('Oturum bulunamadı, lütfen giriş yapın.');

    const res = await fetch(`${API_BASE}/api/projects/${projectId}/submissions`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    if (!res.ok) throw new ProjectApiError(data?.error?.message || 'Gönderim geçmişi alınamadı.');
    return data.submissions;
  },

  async submitProject(
    projectId: string,
    code: string,
    runtimeOutput: string | null
  ): Promise<SubmitProjectResponse> {
    const token = await authService.getAccessToken();
    if (!token) throw new ProjectApiError('Oturum bulunamadı, lütfen giriş yapın.');

    const res = await fetch(`${API_BASE}/api/projects/${projectId}/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ code, language: 'python', runtimeOutput }),
    });

    const data = await res.json();
    if (!res.ok) throw new ProjectApiError(data?.error?.message || 'Proje gönderilemedi.');
    return data;
  },
};

export { ProjectApiError };
